import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, Modal, theme } from 'antd';
import type { InputRef } from 'antd';
import { forwardRef, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

interface ModalComboboxProps {
  value?: string;
  /** Form-controlled path: Ant Form injects value/onChange. */
  onChange?: (value: string) => void;
  /** Fires after a value is committed — e.g. auto-fill remembered prices. */
  onCommit?: (value: string) => void;
  /** Suggestion names (remembered product names). */
  options: string[];
  placeholder?: string;
  /** Modal heading; falls back to the placeholder. */
  title?: string;
  size?: 'small' | 'middle' | 'large';
  style?: CSSProperties;
  maxLength?: number;
  disabled?: boolean;
}

/**
 * A free-text picker that opens its suggestions in a Modal instead of a
 * body-mounted dropdown (which scrolls the page under it now that the shell
 * scrolls inside its own Content box). Unlike ModalSelect this is a combobox:
 * the search field IS the value — the typed text can be committed as a brand
 * new entry, or an existing suggestion tapped. Used for product-name entry on
 * накладные.
 */
export const ModalCombobox = forwardRef<InputRef, ModalComboboxProps>(function ModalCombobox(
  { value, onChange, onCommit, options, placeholder, title, size = 'large', style, maxLength, disabled },
  ref,
) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  const openModal = (): void => {
    if (disabled) return;
    setQuery(value ?? '');
    setOpen(true);
  };
  const close = (): void => setOpen(false);

  const commit = (raw: string): void => {
    const name = raw.trim();
    if (!name) return;
    onChange?.(name);
    onCommit?.(name);
    close();
  };

  return (
    <>
      <Input
        ref={ref}
        readOnly
        value={value ?? ''}
        placeholder={placeholder}
        size={size}
        disabled={disabled}
        style={{ cursor: disabled ? undefined : 'pointer', ...style }}
        onClick={openModal}
      />
      <Modal
        open={open}
        onCancel={close}
        title={title ?? placeholder ?? t('common.select')}
        footer={null}
        destroyOnClose
        zIndex={1100}
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder={placeholder}
            value={query}
            maxLength={maxLength}
            size={size}
            onChange={(e) => setQuery(e.target.value)}
            onPressEnter={() => commit(query)}
          />
          <Button type="primary" size={size} disabled={!query.trim()} onClick={() => commit(query)}>
            {t('common.done')}
          </Button>
        </div>
        <div
          style={{
            maxHeight: '60vh',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {filtered.map((o) => {
            const selected = o === value;
            return (
              <div
                key={o}
                onClick={() => commit(o)}
                style={{
                  padding: '12px 4px',
                  borderTop: `1px solid ${token.colorBorderSecondary}`,
                  cursor: 'pointer',
                  color: selected ? token.colorPrimary : token.colorText,
                  fontWeight: selected ? 600 : 400,
                }}
              >
                {o}
              </div>
            );
          })}
        </div>
      </Modal>
    </>
  );
});
